/**
 * One shared model for the whole app: the bootstrap payload, the live basket, the debounced
 * recommendation and the commit result.
 *
 * The basket id lives in `localStorage` because a phone in a supermarket reloads — the screen locks,
 * Firefox evicts the tab, the shop's wifi drops — and losing a half-scanned basket would make the
 * app useless in exactly the situation it exists for.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ApiError, api, errorMessage, isAbort, onAuthLost, type Bootstrap } from "./api.ts";
import { useApi, useDebounced } from "./hooks.ts";
import { feedback } from "./scanner.ts";
import type { Basket, BasketLine, Category, CommitResult, Recommendation } from "../../server/types.ts";

const BASKET_KEY = "bonusbox.basketId";

/** The last scan, so the scan screen can confirm or complain without owning the request. */
export type ScanEcho = {
	barcode: string;
	line: BasketLine | null;
	error: string | null;
	at: number;
};

export type Model = {
	boot: Bootstrap | null;
	bootError: string | null;
	bootLoading: boolean;
	reloadBoot: () => void;

	categories: Category[];
	setCategories: (next: Category[]) => void;

	basket: Basket | null;
	basketError: string | null;
	basketBusy: boolean;
	lastScan: ScanEcho | null;
	scan: (barcode: string, qty?: number) => Promise<void>;
	setQty: (lineId: number, qty: number) => Promise<void>;
	removeLine: (lineId: number) => Promise<void>;
	chooseCategory: (categoryId: number | null) => Promise<void>;
	startNewBasket: () => Promise<void>;

	rec: Recommendation | null;
	recError: string | null;
	recLoading: boolean;
	/** `false` while the basket has moved on from what the shown recommendation was computed for. */
	recFresh: boolean;
	reloadRec: () => void;

	commitResult: CommitResult | null;
	committing: boolean;
	commitError: string | null;
	commit: (accountId?: number) => Promise<void>;
	clearCommit: () => void;

	authExpired: boolean;
};

const Context = createContext<Model | null>(null);

export function useModel(): Model {
	const model = useContext(Context);
	if (model === null) throw new Error("useModel used outside ModelProvider");
	return model;
}

export function ModelProvider({ children }: { children: ReactNode }) {
	const boot = useApi((signal) => api.bootstrap({ signal }), []);
	const [categories, setCategories] = useState<Category[]>([]);
	const [authExpired, setAuthExpired] = useState(false);

	const [basket, setBasket] = useState<Basket | null>(null);
	const [basketError, setBasketError] = useState<string | null>(null);
	const [basketBusy, setBasketBusy] = useState(false);
	const [lastScan, setLastScan] = useState<ScanEcho | null>(null);
	const basketId = useRef<number | null>(null);
	basketId.current = basket?.id ?? null;

	const [commitResult, setCommitResult] = useState<CommitResult | null>(null);
	const [committing, setCommitting] = useState(false);
	const [commitError, setCommitError] = useState<string | null>(null);

	useEffect(() => {
		onAuthLost(() => setAuthExpired(true));
	}, []);

	useEffect(() => {
		if (boot.data !== null) setCategories(boot.data.categories);
	}, [boot.data]);

	// Resume the stored basket, or open a fresh one. A committed basket is finished, so resuming it
	// would put a stale trip back on screen with no way to reach its barcode again.
	useEffect(() => {
		if (boot.data === null) return;
		let cancelled = false;
		void (async () => {
			const stored = Number(window.localStorage.getItem(BASKET_KEY));
			if (Number.isSafeInteger(stored) && stored > 0) {
				try {
					const existing = await api.basket(stored);
					if (cancelled) return;
					if (existing.committedTripId === null) {
						setBasket(existing);
						return;
					}
				} catch (err) {
					if (cancelled) return;
					if (!(err instanceof ApiError) || (err.status !== 404 && err.status !== 409)) {
						setBasketError(errorMessage(err));
						return;
					}
				}
			}
			try {
				const created = await api.createBasket();
				if (cancelled) return;
				window.localStorage.setItem(BASKET_KEY, String(created.id));
				setBasket(created);
			} catch (err) {
				if (!cancelled) setBasketError(errorMessage(err));
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [boot.data]);

	const mutate = useCallback(async (run: (id: number) => Promise<Basket>): Promise<void> => {
		const id = basketId.current;
		if (id === null) return;
		setBasketBusy(true);
		try {
			setBasket(await run(id));
			setBasketError(null);
		} catch (err) {
			if (!isAbort(err)) setBasketError(errorMessage(err));
		} finally {
			setBasketBusy(false);
		}
	}, []);

	const scan = useCallback(async (barcode: string, qty?: number): Promise<void> => {
		const id = basketId.current;
		if (id === null) return;
		setBasketBusy(true);
		try {
			const result = await api.scan(id, barcode, qty);
			setBasket(result.basket);
			setBasketError(null);
			setLastScan({ barcode, line: result.line, error: null, at: Date.now() });
			// An unresolved barcode still enters the basket, but it buys nothing, so say so audibly.
			if (result.line === null || result.line.productId === null) feedback("miss");
		} catch (err) {
			if (isAbort(err)) return;
			feedback("miss");
			const message = errorMessage(err);
			setBasketError(message);
			setLastScan({ barcode, line: null, error: message, at: Date.now() });
		} finally {
			setBasketBusy(false);
		}
	}, []);

	const setQty = useCallback(
		(lineId: number, qty: number) => mutate((id) => api.setLineQty(id, lineId, qty)),
		[mutate],
	);
	const removeLine = useCallback((lineId: number) => mutate((id) => api.removeLine(id, lineId)), [mutate]);
	const chooseCategory = useCallback(
		(categoryId: number | null) => mutate((id) => api.setBasketCategory(id, categoryId)),
		[mutate],
	);

	const startNewBasket = useCallback(async (): Promise<void> => {
		setBasketBusy(true);
		try {
			const created = await api.createBasket();
			window.localStorage.setItem(BASKET_KEY, String(created.id));
			setBasket(created);
			setBasketError(null);
			setLastScan(null);
			setCommitResult(null);
			setCommitError(null);
		} catch (err) {
			setBasketError(errorMessage(err));
		} finally {
			setBasketBusy(false);
		}
	}, []);

	// One token for "the recommendation would come out different now". Debounced so a burst of
	// scans costs one solve instead of one per item.
	const revision =
		basket === null || basket.lines.length === 0 || basket.committedTripId !== null
			? null
			: `${basket.id}/${basket.updatedAt}/${basket.categoryId ?? "none"}/${basket.lines.length}`;
	const settled = useDebounced(revision, 650);
	const liveId = basket?.id ?? null;
	const rec = useApi(
		settled === null || revision === null
			? null
			: async (signal) => ({
					revision: settled,
					recommendation: await api.recommendation(Number(settled.split("/")[0]), { signal }),
				}),
		[settled, revision === null],
	);

	const commit = useCallback(async (accountId?: number): Promise<void> => {
		const id = basketId.current;
		if (id === null) return;
		setCommitting(true);
		setCommitError(null);
		try {
			const result = await api.commit(id, accountId);
			setCommitResult(result);
			if (result.verified) feedback("hit");
			else feedback("miss");
			// The basket is now committed; re-read it so the UI stops offering to change it.
			setBasket(await api.basket(id));
		} catch (err) {
			if (isAbort(err)) return;
			feedback("miss");
			setCommitError(errorMessage(err));
		} finally {
			setCommitting(false);
		}
	}, []);

	const clearCommit = useCallback(() => {
		setCommitResult(null);
		setCommitError(null);
	}, []);

	const model = useMemo<Model>(
		() => ({
			boot: boot.data,
			bootError: boot.error,
			bootLoading: boot.loading,
			reloadBoot: boot.reload,
			categories,
			setCategories,
			basket,
			basketError,
			basketBusy,
			lastScan,
			scan,
			setQty,
			removeLine,
			chooseCategory,
			startNewBasket,
			rec: rec.data?.recommendation.basketId === liveId ? rec.data.recommendation : null,
			recError: rec.error,
			recLoading: rec.loading || (revision !== null && revision !== settled),
			recFresh:
				revision !== null &&
				rec.data?.revision === revision &&
				!rec.loading &&
				rec.error === null &&
				!basketBusy,
			reloadRec: rec.reload,
			commitResult,
			committing,
			commitError,
			commit,
			clearCommit,
			authExpired,
		}),
		[
			boot.data,
			boot.error,
			boot.loading,
			boot.reload,
			categories,
			basket,
			basketError,
			basketBusy,
			lastScan,
			scan,
			setQty,
			removeLine,
			chooseCategory,
			startNewBasket,
			rec.data,
			rec.error,
			rec.loading,
			rec.reload,
			revision,
			settled,
			liveId,
			commitResult,
			committing,
			commitError,
			commit,
			clearCommit,
			authExpired,
		],
	);

	return <Context.Provider value={model}>{children}</Context.Provider>;
}
